import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { uiBtn } from '../../../ui/buttonClasses';
import { CHART_TOOLTIP_CLASS } from './chartTooltipClasses';

interface StrokeCycle {
  start: number;
  end: number;
  strokeNumber: number;
}

interface PlayerControlsProps {
  isPlaying: boolean;
  progress: number;
  speed: number;
  hasData: boolean;
  windowRangeLabel: string;
  onPlayPause: () => void;
  onReset: () => void;
  onSpeedChange: (speed: number) => void;
  onProgressChange: (progress: number) => void;
  onResetZoom: () => void;
  // Stroke data for hover tooltip
  allStrokeCycles?: StrokeCycle[];
  timeExtent?: [number, number];
  animationRange?: number;
  onStrokeClick?: (strokeNumber: number) => void;
  // Per stroke mode toggle
  perStrokeMode?: boolean;
  onPerStrokeModeToggle?: () => void;
}

const PlayerControls: React.FC<PlayerControlsProps> = ({
  isPlaying,
  progress,
  speed,
  hasData,
  windowRangeLabel,
  onPlayPause,
  onReset,
  onSpeedChange,
  onProgressChange,
  onResetZoom,
  allStrokeCycles = [],
  timeExtent,
  animationRange,
  onStrokeClick,
  perStrokeMode = true,
  onPerStrokeModeToggle,
}) => {
  const progressTrackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(400); // Start smaller to ensure responsive sizing works
  const [hoverStroke, setHoverStroke] = useState<{ strokeNumber: number; x: number } | null>(null);
  const scrubStartPositionRef = useRef<number | null>(null);

  // Track container width for responsive sizing
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0) {
        setContainerWidth(rect.width);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(element);
    updateWidth(); // Set initial width immediately

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate responsive styles based on container width (matching PowerAnalysisControls pattern)
  const responsiveStyles = useMemo(() => {
    if (containerWidth < 400) {
      return {
        fontSize: '0.625rem', // 10px
        padding: { x: '0.375rem', y: '0.25rem' }, // px-1.5 py-1
        iconSize: '0.75rem', // 12px
        gap: '0.25rem', // gap-1
        spacing: '0.375rem', // space-y-1.5
      };
    }
    if (containerWidth < 600) {
      return {
        fontSize: '0.75rem', // 12px
        padding: { x: '0.5rem', y: '0.375rem' }, // px-2 py-1.5
        iconSize: '0.875rem', // 14px
        gap: '0.375rem', // gap-1.5
        spacing: '0.5rem', // space-y-2
      };
    }
    if (containerWidth < 800) {
      return {
        fontSize: '0.8125rem', // 13px
        padding: { x: '0.625rem', y: '0.4375rem' }, // px-2.5 py-1.75
        iconSize: '1rem', // 16px
        gap: '0.5rem', // gap-2
        spacing: '0.625rem', // space-y-2.5
      };
    }
    // Large screens
    return {
      fontSize: '0.875rem', // 14px (text-sm)
      padding: { x: '0.5rem', y: '0.25rem' }, // px-3 py-1.5 (more compressed vertically)
      iconSize: '1rem', // 16px
      gap: '0.5rem', // gap-2
      spacing: '0.75rem', // space-y-3
    };
  }, [containerWidth]);

  const updateProgressFromClientX = useCallback(
    (clientX: number) => {
      const track = progressTrackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      onProgressChange(ratio * 100);
    },
    [onProgressChange]
  );

  // Find stroke at a given time
  const findStrokeAtTime = useCallback(
    (time: number): StrokeCycle | null => {
      if (!allStrokeCycles || allStrokeCycles.length === 0) return null;
      return allStrokeCycles.find((stroke) => stroke.start <= time && time <= stroke.end) || null;
    },
    [allStrokeCycles]
  );

  const handleScrubStart = useCallback((clientX: number) => {
    setIsScrubbing(true);
    scrubStartPositionRef.current = clientX;
    // Don't update progress immediately - wait to see if it's a click or drag
    // This prevents clearing selectedStrokeIndex before onStrokeClick sets it
  }, []);

  useEffect(() => {
    if (!isScrubbing) return undefined;
    const handleMove = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.buttons === 0) return;
      // Only update progress if we've moved (it's a drag, not a click)
      updateProgressFromClientX(event.clientX);
    };
    const stopScrub = (event: PointerEvent) => {
      setIsScrubbing(false);

      // Detect if this was a click (no significant movement) vs drag
      if (scrubStartPositionRef.current !== null) {
        const movement = Math.abs(event.clientX - scrubStartPositionRef.current);
        const CLICK_THRESHOLD = 5; // pixels - if moved less than this, consider it a click

        if (movement < CLICK_THRESHOLD) {
          // It was a click
          if (perStrokeMode && onStrokeClick) {
            // Per-stroke mode: find stroke and show it
            const track = progressTrackRef.current;
            if (track && timeExtent && animationRange) {
              const rect = track.getBoundingClientRect();
              const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
              const time = timeExtent[0] + ratio * animationRange;
              const stroke = findStrokeAtTime(time);
              if (stroke) {
                onStrokeClick(stroke.strokeNumber);
              }
            }
          } else {
            // Normal mode: update progress (same as drag)
            updateProgressFromClientX(event.clientX);
          }
        } else if (movement >= CLICK_THRESHOLD) {
          // It was a drag - update progress now
          updateProgressFromClientX(event.clientX);
        }
      }

      scrubStartPositionRef.current = null;
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stopScrub);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stopScrub);
    };
  }, [
    isScrubbing,
    updateProgressFromClientX,
    onStrokeClick,
    timeExtent,
    animationRange,
    findStrokeAtTime,
    perStrokeMode,
  ]);

  const handleSpeedChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSpeedChange(parseFloat(e.target.value));
  };

  // Handle hover on progress track
  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      // Don't show tooltip while scrubbing
      if (isScrubbing) {
        setHoverStroke(null);
        return;
      }

      const track = progressTrackRef.current;
      if (!track || !timeExtent || !animationRange || allStrokeCycles.length === 0) {
        setHoverStroke(null);
        return;
      }

      const rect = track.getBoundingClientRect();
      const clientX = event.clientX;
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      const time = timeExtent[0] + ratio * animationRange;

      const stroke = findStrokeAtTime(time);
      if (stroke) {
        setHoverStroke({
          strokeNumber: stroke.strokeNumber,
          x: clientX - rect.left, // Position relative to track
        });
      } else {
        setHoverStroke(null);
      }
    },
    [isScrubbing, timeExtent, animationRange, allStrokeCycles, findStrokeAtTime]
  );

  const handlePointerLeave = useCallback(() => {
    setHoverStroke(null);
  }, []);

  return (
    <div
      ref={containerRef}
      className="chart-toolbar"
      style={{
        padding: responsiveStyles.spacing,
        display: 'flex',
        flexDirection: 'column',
        gap: responsiveStyles.spacing,
      }}
    >
      <div
        className="chart-toolbar-inner flex flex-col sm:flex-row sm:items-center sm:justify-between flex-wrap"
        style={{
          padding: responsiveStyles.spacing,
          gap: responsiveStyles.gap,
        }}
      >
        <div className="flex items-center flex-wrap" style={{ gap: responsiveStyles.gap }}>
          {/* Per Stroke Mode Toggle */}
          {onPerStrokeModeToggle && (
            <button
              type="button"
              onClick={onPerStrokeModeToggle}
              disabled={!hasData}
              className={`${uiBtn('toggle', { pressed: perStrokeMode, size: 'sm' })} flex items-center whitespace-nowrap min-w-[2rem] sm:min-w-0`}
              style={{
                fontSize: responsiveStyles.fontSize,
                padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
                gap: '0.5rem',
              }}
              title={perStrokeMode ? 'Per Stroke Mode: ON' : 'Per Stroke Mode: OFF'}
            >
              <svg
                className="flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ width: responsiveStyles.iconSize, height: responsiveStyles.iconSize }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span className="hidden sm:inline" style={{ fontSize: responsiveStyles.fontSize }}>
                Per Stroke
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={onPlayPause}
            disabled={!hasData}
            className={`${uiBtn('primary', { size: 'sm' })} flex items-center whitespace-nowrap min-w-[2rem] sm:min-w-0`}
            style={{
              fontSize: responsiveStyles.fontSize,
              padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
              gap: '0.5rem',
            }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <>
                <svg
                  className="flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  style={{ width: responsiveStyles.iconSize, height: responsiveStyles.iconSize }}
                >
                  <rect x="4" y="3" width="3" height="10" />
                  <rect x="9" y="3" width="3" height="10" />
                </svg>
                <span className="hidden sm:inline" style={{ fontSize: responsiveStyles.fontSize }}>
                  Pause
                </span>
              </>
            ) : (
              <>
                <svg
                  className="flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  style={{ width: responsiveStyles.iconSize, height: responsiveStyles.iconSize }}
                >
                  <polygon points="5,3 13,8 5,13" />
                </svg>
                <span className="hidden sm:inline" style={{ fontSize: responsiveStyles.fontSize }}>
                  Play
                </span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onReset}
            disabled={!hasData}
            className={`${uiBtn('neutral', { size: 'sm' })} flex items-center whitespace-nowrap min-w-[2rem] sm:min-w-0`}
            style={{
              fontSize: responsiveStyles.fontSize,
              padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
              gap: '0.5rem',
            }}
            title="Reset"
          >
            <svg
              className="flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ width: responsiveStyles.iconSize, height: responsiveStyles.iconSize }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline" style={{ fontSize: responsiveStyles.fontSize }}>
              Reset
            </span>
          </button>
          <button
            type="button"
            onClick={onResetZoom}
            disabled={!hasData}
            className={`${uiBtn('neutral', { size: 'sm' })} flex items-center whitespace-nowrap min-w-[2rem] sm:min-w-0`}
            style={{
              fontSize: responsiveStyles.fontSize,
              padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
              gap: '0.5rem',
            }}
            title="Reset Zoom"
          >
            <svg
              className="flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ width: responsiveStyles.iconSize, height: responsiveStyles.iconSize }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
              />
            </svg>
            <span className="hidden sm:inline" style={{ fontSize: responsiveStyles.fontSize }}>
              Reset Zoom
            </span>
          </button>
        </div>

        <div className="flex items-center flex-wrap" style={{ gap: responsiveStyles.gap }}>
          <span
            className="text-content-muted whitespace-nowrap"
            style={{ fontSize: responsiveStyles.fontSize }}
            title="Zoom and pan shortcut"
          >
            Shift + Scroll to zoom • Drag to pan
          </span>
          <div className="flex items-center" style={{ gap: responsiveStyles.gap }}>
            <label
              className="text-content-secondary whitespace-nowrap"
              style={{ fontSize: responsiveStyles.fontSize }}
            >
              Speed:
            </label>
            <select
              value={speed}
              onChange={handleSpeedChangeSelect}
              className="filter-control"
              style={{
                fontSize: responsiveStyles.fontSize,
                padding: `${responsiveStyles.padding.y} ${responsiveStyles.padding.x}`,
              }}
            >
              <option value={0.1}>0.10x</option>
              <option value={0.25}>0.25x</option>
              <option value={0.5}>0.50x</option>
              <option value={0.75}>0.75x</option>
              <option value={1}>1.0x</option>
              <option value={1.5}>1.5x</option>
              <option value={2}>2.0x</option>
            </select>
          </div>
          <span
            className="text-content-secondary whitespace-nowrap"
            style={{ fontSize: responsiveStyles.fontSize }}
          >
            Progress: {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: responsiveStyles.spacing }}>
        <div
          ref={progressTrackRef}
          className="relative w-full bg-gray-200 rounded-full cursor-pointer select-none"
          style={{ height: containerWidth < 600 ? '0.375rem' : '0.5rem' }}
          onPointerDown={(event) => handleScrubStart(event.clientX)}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
        >
          <div
            className="bg-accent h-full rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
          {/* Stroke markers - small blue dots (only in per-stroke mode) */}
          {perStrokeMode &&
            allStrokeCycles &&
            allStrokeCycles.length > 0 &&
            timeExtent &&
            animationRange && (
              <>
                {allStrokeCycles.map((stroke) => {
                  const strokeCenterTime = (stroke.start + stroke.end) / 2;
                  const strokeProgress =
                    ((strokeCenterTime - timeExtent[0]) / animationRange) * 100;
                  return (
                    <div
                      key={stroke.strokeNumber}
                      className="absolute top-1/2 -translate-y-1/2 rounded-full pointer-events-none bg-[rgb(var(--color-text-muted))]"
                      style={{
                        width: containerWidth < 600 ? '2px' : '3px',
                        height: containerWidth < 600 ? '2px' : '3px',
                        left: `calc(${strokeProgress}% - ${containerWidth < 600 ? '1px' : '1.5px'})`,
                      }}
                    />
                  );
                })}
              </>
            )}
          <div
            className="absolute top-1/2 rounded-full bg-surface border border-accent shadow -translate-y-1/2 cursor-pointer"
            style={{
              width: containerWidth < 600 ? '0.625rem' : '0.75rem',
              height: containerWidth < 600 ? '0.625rem' : '0.75rem',
              left: `calc(${progress}% - ${containerWidth < 600 ? '3px' : '4px'})`,
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
              handleScrubStart(event.clientX);
            }}
          />
          {/* Hover tooltip showing stroke number */}
          {hoverStroke && (
            <div
              className={`${CHART_TOOLTIP_CLASS} font-semibold`}
              style={{
                left: `${hoverStroke.x}px`,
                bottom: '100%',
                marginBottom: '8px',
                transform: 'translateX(-50%)',
              }}
            >
              Stroke {hoverStroke.strokeNumber}
              {/* Tooltip arrow */}
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent"
                style={{ marginTop: '-1px', borderTopColor: 'rgb(var(--chart-tooltip-bg))' }}
              />
            </div>
          )}
        </div>
      </div>

      {hasData && windowRangeLabel && (
        <div
          className="flex justify-end text-gray-600"
          style={{ fontSize: responsiveStyles.fontSize }}
        >
          <span className="whitespace-nowrap">Session: {windowRangeLabel}</span>
        </div>
      )}
    </div>
  );
};

export default PlayerControls;
